import { Injectable } from '@angular/core';
import { map, Observable, tap } from 'rxjs';

import {
	AbstractCRUDService,
	AlarisApiService,
	AlarisLanguageService,
	AlarisToasterService,
	ErrorNotifierConfig,
	ExtendableRefBookService,
} from '@campaign-portal/components-library';

import {
	CreateResponse,
	DeleteRequest,
	DeleteResponse,
	ReadResponse,
} from '@campaign-portal/namespace/common/implementations';
import { RPCRequestParams } from '@campaign-portal/namespace/common/rpc.params';
import { Id, UUID } from '@campaign-portal/namespace/common/id';
import { UsersResetPasswordRequest, UsersResetPasswordResponse } from '@campaign-portal/namespace/entities/users/specs';
import { ResetPasswordRequest, ResetPasswordResponse } from '@campaign-portal/namespace/entities/auth/specs';
import { User } from '@campaign-portal/namespace/common/user';
import { UserEntity } from '../types';

@Injectable({
	providedIn: 'root',
})
export class UsersService extends ExtendableRefBookService<UserEntity<UUID>> implements AbstractCRUDService {
	readonly create = this.update;

	constructor(
		private readonly api: AlarisApiService,
		private readonly alarisToaster: AlarisToasterService,
		private readonly langService: AlarisLanguageService
	) {
		super();
	}

	get entity(): string {
		return this.langService.translate('notifications.entities.user');
	}

	get title(): string {
		return this.langService.translate('notifications.titles.user');
	}

	override load(): Observable<ReadResponse<UserEntity<UUID>[]>> {
		return this.api.loader<ReadResponse<User<UUID>[]>>('Users.Read', {}, this.loading$, this.errorNotifier).pipe(
			map((resp) => {
				const Data = resp.Data.map((user) => this.toUserEntity(user));
				return super.process({ ...resp, Data });
			})
		);
	}

	read(params?: RPCRequestParams): Observable<ReadResponse<UserEntity<UUID>[]>> {
		return this.api.loader<ReadResponse<User<UUID>[]>>('Users.Read', params, this.loading$, this.errorNotifier).pipe(
			map((resp) => {
				const Data = resp.Data.map((user) => this.toUserEntity(user));
				return { ...resp, Data };
			})
		);
	}

	private toUserEntity(user: User<UUID>): UserEntity<UUID> {
		return new UserEntity(user);
	}

	update(user: User): Observable<CreateResponse<User>> {
		const notify = (response: CreateResponse<User>): void => {
			if (response.Success) {
				const message = this.langService.translate(
					user.id ? 'notifications.actions.update' : 'notifications.actions.create',
					{
						entity: this.entity,
						name: user.details.firstName ?? '',
					}
				);
				this.alarisToaster.success(message, this.title);
			}
		};
		return this.api
			.loader<
				CreateResponse<User>
			>(`Users.${user.id ? 'Update' : 'Create'}`, { Data: { Entities: [user] } }, this.loading$, this.errorNotifier, notify)
			.pipe(
				tap(() => {
					this.refresh$.next();
				})
			);
	}

	delete(id: Id<UUID>): Observable<DeleteResponse<User>> {
		const params: DeleteRequest<User<UUID>> = { Data: { Ids: [id] } };
		const notify = (response: DeleteResponse<User>): void => {
			if (response.Success) {
				const message = this.langService.translate('notifications.actions.delete', { entity: this.entity });
				this.alarisToaster.success(message, this.title);
			}
		};
		return this.api
			.loader<DeleteResponse<User>>('Users.Delete', params, this.loading$, this.errorNotifier, notify)
			.pipe(
				tap(() => {
					this.refresh$.next();
				})
			);
	}

	passwordResetByHand(params: UsersResetPasswordRequest): Observable<UsersResetPasswordResponse> {
		const notify = (response: UsersResetPasswordResponse): void => {
			if (response.Success) {
				const message = this.langService.translate('notifications.actions.reset');
				this.alarisToaster.success(message, this.title);
			}
		};
		return this.api.loader<UsersResetPasswordResponse>(
			'Users.ResetPassword',
			params,
			this.loading$,
			this.errorNotifier,
			notify
		);
	}

	passwordResetByEmail(params: ResetPasswordRequest): Observable<ResetPasswordResponse> {
		const notify = (response: ResetPasswordResponse): void => {
			if (response.Success) {
				const message = this.langService.translate('notifications.actions.resetPasswordByEmail');
				this.alarisToaster.success(message, this.title);
			}
		};
		return this.api.loader<ResetPasswordResponse>(
			'Auth.ResetPassword',
			params,
			this.loading$,
			this.errorNotifier,
			notify
		);
	}

	resetCode(params: ResetPasswordRequest): Observable<ResetPasswordResponse> {
		const notify = (response: ResetPasswordResponse): void => {
			if (response.Success) {
				const message = this.langService.translate('notifications.actions.resetCodeByEmail');
				this.alarisToaster.success(message, this.title);
			}
		};
		return this.api.loader<ResetPasswordResponse>(
			'Auth.AdminResetCodeForUser',
			params,
			this.loading$,
			this.errorNotifier,
			notify
		);
	}

	private readonly errorNotifier = (): ErrorNotifierConfig => ({ title: this.title });
}
